import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { WizardStep } from '@/components/postgrid/wizard';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';

const detailsSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .or(z.literal('')),
  phone_number: z.string().optional(),
});

export type DetailsStepData = z.infer<typeof detailsSchema>;

interface ContactDetailsStepProps {
  wizardData?: Partial<DetailsStepData>;
  updateStepData?: (data: Record<string, any>) => void;
  markStepComplete?: (isValid: boolean) => void;
}

export const ContactDetailsStep: React.FC<ContactDetailsStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
}) => {
  const {
    register,
    watch,
    formState: { errors, isValid },
  } = useForm<DetailsStepData>({
    resolver: zodResolver(detailsSchema),
    mode: 'onChange',
    defaultValues: {
      email: wizardData.email ?? '',
      phone_number: wizardData.phone_number ?? '',
    },
  });

  const watched = watch();

  useEffect(() => {
    updateStepData?.(watched);
  }, [watched, updateStepData]);

  useEffect(() => {
    markStepComplete?.(true); // Not strictly required fields – always allow proceed
  }, [markStepComplete]);

  return (
    <WizardStep
      title="How can we reach them?"
      description="Add an email or phone number so we can contact them. Both are optional."
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <Fieldset>
          <FieldGroup className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field>
              <Label>Email Address</Label>
              <Input
                {...register('email')}
                placeholder="john@example.com"
                type="email"
              />
              {errors.email && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.email.message}
                </p>
              )}
            </Field>

            <Field>
              <Label>Phone Number</Label>
              <Input
                {...register('phone_number')}
                placeholder="(555) 123-4567"
                type="tel"
              />
              {errors.phone_number && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.phone_number.message}
                </p>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </div>
    </WizardStep>
  );
};
